import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import "./MaleLevelPage.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TopBar2 from "../common/TopBar2";
import MaleLevelFrame, {
  MALE_RANK_SVGA_URLS,
  MaleRankSvgaHost,
} from "./MaleLevelFrame";
import goldCoin from "../../assets/gold.png";
import { Lock } from "lucide-react";

/* ===================== Image Plan ===================== */
import { storage } from "../../firebase";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

/* ===================== Levels Logic ===================== */
import {
  getMaleLevelList,
  computeLevelFromPoints,
  getNextThreshold,
} from "../../config/maleLevels";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";
import { formatNumberEn } from "../../utils/digits";

/* ===================== Helpers ===================== */
const getMalePoints = (user) =>
  Number(
    user?.malePoints ??
      user?.maleTotalRecharge ??
      user?.maleXp ??
      user?.maleScore ??
      user?.totalSpent ??
      0
  );

const getMaleLevel = (user, points) => {
  const lvRaw = user?.maleLevel;
  const lv = Number(lvRaw);

  if (Number.isFinite(lv)) return lv;
  return computeLevelFromPoints(Number(points || 0));
};

/* ===================== Image helpers ===================== */
function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

function storagePathFromValue(value) {
  if (!value || typeof value !== "string") return "";

  if (!isHttpUrl(value) && !value.startsWith("gs://")) return value;
  if (value.startsWith("gs://")) return value;

  try {
    const part = value.split("/o/")[1];
    if (!part) return "";
    return decodeURIComponent((part.split("?")[0] || "").trim());
  } catch {
    return "";
  }
}

async function resolveToDownloadUrl(value) {
  if (!value) return "";

  if (isHttpUrl(value) && !value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  if (isHttpUrl(value) && value.includes("firebasestorage.googleapis.com")) {
    return value;
  }

  try {
    const pathOrGs = storagePathFromValue(value) || value;
    return await getDownloadURL(storageRef(storage, pathOrGs));
  } catch (e) {
    console.warn("MaleLevelPage resolveToDownloadUrl failed:", value, e);
    return "";
  }
}

const getMaleAvatarRaw = (user) =>
  user?.profileImage ||
  user?.photoURL ||
  user?.profileImageUrl ||
  user?.avatar ||
  user?.avatarUrl ||
  user?.image ||
  user?.imageUrl ||
  user?.photo ||
  "";

/* ===================== Static Levels ===================== */
const LEVEL_LIST = getMaleLevelList();

/* ===================== Utils ===================== */
function formatNumber(n) {
  return formatNumberEn(Number(n || 0));
}

function getSafeTranslatedLevelName(t, level, fallbackName) {
  const rankPrefix = t("rank_prefix", { defaultValue: "R" });
  const rankFallback = `${rankPrefix}${level}`;
  const translated = t(`lv${level}`, {
    defaultValue: fallbackName || rankFallback,
  });

  return typeof translated === "string"
    ? translated
    : fallbackName || rankFallback;
}

/* ===================== Page ===================== */
export default function MaleLevelPage({ user, onBack }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [previewLevel, setPreviewLevel] = useState(null);
  const [resolvedAvatar, setResolvedAvatar] = useState(() =>
    defaultAvatarByGender("male")
  );
  const orbitScrollerRef = useRef(null);
  const orbitItemRefs = useRef({});

  const handleBack = useCallback(() => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (window.history && window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/profile");
  }, [onBack]);

  const points = useMemo(() => getMalePoints(user), [user]);
  const computedLevel = useMemo(() => getMaleLevel(user, points), [user, points]);
  const shownLevel = previewLevel ?? computedLevel;

  /** UI-only: never show Lv0 / R0 — real 0 maps to 1 for labels & frames */
  const displayLevel = useMemo(
    () => Math.max(1, Number(shownLevel) || 0),
    [shownLevel]
  );

  const activeCarouselKey = displayLevel;

  const currentObj = useMemo(
    () => LEVEL_LIST.find((x) => x.level === shownLevel),
    [shownLevel]
  );

  const nameObj = useMemo(
    () => LEVEL_LIST.find((x) => x.level === displayLevel),
    [displayLevel]
  );

  const currentReq = Number(currentObj?.required ?? 0);

  const currentName = useMemo(() => {
    return getSafeTranslatedLevelName(t, displayLevel, nameObj?.name);
  }, [t, displayLevel, nameObj]);

  const next = useMemo(() => getNextThreshold(shownLevel, points), [shownLevel, points]);
  const nextReq = Number(next?.required ?? currentReq);

  const progressRatio = useMemo(() => {
    if (!next) return 1;
    const denom = Math.max(1, nextReq - currentReq);
    const raw = (points - currentReq) / denom;
    return Math.max(0, Math.min(1, raw));
  }, [points, currentReq, nextReq, next]);

  const remainingToNext = useMemo(() => {
    if (!next) return 0;
    return Math.max(0, nextReq - points);
  }, [next, nextReq, points]);

  const pickLevel = useCallback((lvl) => {
    setPreviewLevel(lvl);
    window.clearTimeout(pickLevel.__t);
    pickLevel.__t = window.setTimeout(() => setPreviewLevel(null), 2500);
  }, []);

  // eslint-disable-next-line
  pickLevel.__t = pickLevel.__t;

  const levelCards = useMemo(() => {
    return LEVEL_LIST.filter((x) => x.level >= 1 && x.level <= 10).sort(
      (a, b) => a.level - b.level
    );
  }, []);

  /* ===================== Resolve avatar ===================== */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const rawAvatar = getMaleAvatarRaw(user);
      const maleFb = defaultAvatarByGender("male");

      if (!rawAvatar || rawAvatar === "/images/default-avatar.png") {
        if (!cancelled) setResolvedAvatar(maleFb);
        return;
      }

      const finalUrl =
        (await resolveToDownloadUrl(rawAvatar)) || maleFb;

      if (!cancelled) {
        setResolvedAvatar(finalUrl);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user]);

  /* ===================== Auto-center active card ===================== */
  useEffect(() => {
    const scroller = orbitScrollerRef.current;
    const activeEl = orbitItemRefs.current?.[activeCarouselKey];

    if (!scroller || !activeEl) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();

    const currentScroll = scroller.scrollLeft;
    const delta =
      activeRect.left -
      scrollerRect.left -
      scrollerRect.width / 2 +
      activeRect.width / 2;

    scroller.scrollTo({
      left: currentScroll + delta,
      behavior: "smooth",
    });
  }, [activeCarouselKey]);

  if (!user) {
    return (
      <div className="maleLevelPage">
        <div className="maleLevelSkeleton maleLevelSkeletonHero" />
        <div className="maleLevelSkeleton maleLevelSkeletonCarousel" />
      </div>
    );
  }

  return (
    <div className="maleLevelPage">
      <div className="maleLevelStarsBg" />
      <div className="maleLevelNebula maleLevelNebulaTop" />
      <div className="maleLevelNebula maleLevelNebulaBottom" />
      <div className="maleLevelGlowBlob maleLevelGlowBlobA" aria-hidden />
      <div className="maleLevelGlowBlob maleLevelGlowBlobB" aria-hidden />

      <TopBar2
        title={t("rank_title", { defaultValue: "سلم الرتب" })}
        onBack={handleBack}
      />

      <section className="maleLevelHero">
        <div className="maleLevelAvatarWrap">
          <div className="maleLevelHeroFrameSizer">
         <MaleLevelFrame
  key={displayLevel}
  displayLevel={displayLevel}
  imageUrl={resolvedAvatar}
  size="clamp(190px, 52vw, 230px)"
  showBadge
/>
          </div>
        </div>

        <div className="maleLevelHeroCard">
          <div className="maleLevelHeroIntro">
            <div className="maleLevelHeroEyebrow">
              {t("rank_journey", {
                defaultValue: "مسارك داخل LIVORA",
              })}
            </div>

            <div className="maleLevelHeroName">{currentName}</div>

            <div className="maleLevelHeroRankTag">
              {t("rank_prefix", { defaultValue: "R" })}
              {displayLevel}
            </div>

            <div className="maleLevelHeroSub">
              {next
                ? t("rank_sub_next", {
                    defaultValue: "واصل التقدم لفتح الرتبة التالية.",
                  })
                : t("rank_sub_done", {
                    defaultValue: "أنت الآن في أعلى رتبة متاحة.",
                  })}
            </div>
          </div>

          <div className="maleLevelProgressBlock">
            <div className="maleLevelProgressTop">
              <span>
                {t("current_rank_stage", { defaultValue: "مرحلتك الحالية" })}
              </span>
              <span className="maleLevelProgressNextHint">
                {t("rank_prefix", { defaultValue: "R" })}
                {next ? next.level : 10}
              </span>
            </div>

            <div className="maleLevelProgressTrack">
              <div
                className="maleLevelProgressFill"
                style={{ width: `${Math.round(progressRatio * 100)}%` }}
              />
            </div>

            <div className="maleLevelProgressHint">
              {next ? (
                <>
                  {t("rank_remaining_points", {
                    defaultValue: "المتبقي للوصول التالي",
                  })}{" "}
                  <strong>{formatNumber(remainingToNext)}</strong>
                </>
              ) : (
                <>
                  {t("max_rank_reached", {
                    defaultValue: "تم بلوغ أعلى رتبة",
                  })}
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            className="maleLevelBigBalance maleLevelBigBalanceAction"
            onClick={() => navigate("/buy-coins")}
          >
            <span className="maleLevelBigBalanceCoin">
              <img
                src={goldCoin}
                alt={t("livora_coin_alt", { defaultValue: "Livora coin" })}
              />
            </span>
            <span className="maleLevelBigBalanceValue">
              {formatNumber(points)}
            </span>
          </button>
        </div>
      </section>

      <section className="maleLevelOrbitSection maleLevelOrbitCarouselRoot">
        <div className="maleLevelOrbitHeader">
          <h3>{t("ranks_title", { defaultValue: "سلم الرتب" })}</h3>
          <p>
            {t("ranks_swipe_hint", {
              defaultValue:
                "اسحب بين البطاقات لمعاينة الرتب المختلفة.",
            })}
          </p>
        </div>

        <div className="maleLevelOrbitScrollerOuter maleLevelOrbitCarouselStrip">
          <div className="maleLevelOrbitScroller" ref={orbitScrollerRef}>
            {levelCards.map((lvl) => {
              const active = lvl.level === displayLevel;
              const isReached = Math.max(1, computedLevel) >= lvl.level;
              const levelName = getSafeTranslatedLevelName(
                t,
                lvl.level,
                lvl.name
              );
              const svgaUrl = MALE_RANK_SVGA_URLS[lvl.level];

              return (
                <button
                  key={lvl.level}
                  type="button"
                  ref={(el) => {
                    orbitItemRefs.current[lvl.level] = el;
                  }}
                 className={`maleLevelOrbitCard maleLevelOrbitCard--rank-${lvl.level} ${
  active ? "isActive" : ""
} ${isReached ? "isReached" : "isLocked"}`}
                  onClick={() => pickLevel(lvl.level)}
                >
                  <div className="maleLevelOrbitFrameSlot maleLevelSvgaFrame">
                    <div className="maleLevelOrbitFrameStack">
                      <MaleRankSvgaHost
                        src={svgaUrl}
                        className="maleLevelOrbitSvgaHost"
                        fallbackClassName="maleLevelOrbitSvgaFallback"
                      />
                    </div>
                  </div>

                  <div className="maleLevelOrbitRankChip">
                    {t("rank_prefix", { defaultValue: "R" })}
                    {lvl.level}
                  </div>

                  <div className="maleLevelOrbitMeta">
                    <div className="maleLevelOrbitName">{levelName}</div>

                    <div className="maleLevelOrbitValue">
                      <img src={goldCoin} alt="" />
                      <span>{formatNumber(lvl.required)}</span>
                    </div>
                  </div>

                  {!isReached ? (
                    <span className="maleLevelOrbitLockOverlay" aria-hidden>
                      <Lock className="maleLevelOrbitLockIcon" />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
